import * as d3 from "d3"
import React, { useContext, useEffect, useState } from "react"
import { WebsocketContext } from "../../../../context/WebsocketContext"
import { isFacilitator } from "../../../../functions/FilterUsers"
import addCircleIcon from "../../../../images/add-circle-icon.svg"
import addSquareIcon from "../../../../images/add-square-icon.svg"
import eyeIcon from "../../../../images/eye-icon.svg"
import eyeSlashIcon from "../../../../images/eye-slash-icon.svg"
import trashIcon from "../../../../images/trash-icon.svg"
import { setCanvas } from "../../../../stores/canvasSlice"
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks"
import { RootState } from "../../../../stores/store"
import { getCircleColor, getDefaultColor, getRectColor, isColliding } from "./utils/PostitUtils"
import negativeIcon from "../../../../images/negative.svg"
import plusIcon from "../../../../images/plus.svg"
import targetIcon from "../../../../images/focus.png"
import Timer from "./utils/timer/Timer"

interface IProps {
  dragging: boolean
  zoomTransform: d3.ZoomTransform | null
  resolution: { width: number; height: number }
  onZoomIn: () => void
  onZoomOut: () => void
  onCenterBoard: () => void
}

const Toolbar = ({ dragging, zoomTransform, resolution, onZoomIn, onZoomOut, onCenterBoard }: IProps): React.JSX.Element => {
  const sessionStore = useAppSelector((state: RootState) => state.session)
  const canvasStore = useAppSelector((state: RootState) => state.canvas)
  const dispatch = useAppDispatch()
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })
  const isFeedbackCompleted = canvasStore.feedback?.status === "completed"

  const { send } = useContext(WebsocketContext)

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const processCardAfterCreation = (card: ICard) => {
    // Aguarda um pouco para o card ser renderizado no DOM
    setTimeout(() => {
      const cardElement = document.getElementById(`hitbox-${card.id}`)
      if (!cardElement) return

      let colliderTarget: SVGGElement | undefined
      const originElement = d3.select(cardElement)

      // Verifica colisões com áreas de categoria
      d3.selectAll<SVGGElement, unknown>(".droppable-target").each(function () {
        const originNode = originElement.node();
        if (originNode && isColliding(originNode as unknown as SVGGElement, this)) {
          colliderTarget = this;
        }
      });

      const category = colliderTarget?.id
      const updatedCard = {
        ...card,
        category,
        color: getRectColor(card),
        last_dragged_by: sessionStore.id,
        initial_x: card.x ?? 0,
        initial_y: card.y ?? 0,
      }

      // Envia a atualização do card processado
      send("editcard", updatedCard)
    }, 100)
  }

  const addCard = (shape: string) => {
    if (!zoomTransform) return

    const screenX = resolution.width / 2
    const screenY = resolution.height / 5

    let [svgX, svgY] = zoomTransform.invert([screenX, screenY])

    if (shape === "circle") {
      svgY -= 50
    }

    const newCard = {
      id: "",
      category: null,
      text: "",
      color: shape === "circle" ? getCircleColor() : getDefaultColor(),
      owner: sessionStore.id,
      shape: shape,
      x: svgX,
      y: svgY,
      initial_x: svgX,
      initial_y: svgY,
      last_dragged_by: sessionStore.id,
      motion_hash: `motion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      font_size: 12,
      text_length: 0,
    } as ICard

    send("createcard", newCard)

    // Processa o card após criação para definir categoria e cor
    processCardAfterCreation(newCard)
  }

  const toggleFeedbackVisibility = (): void => {
    if (!canvasStore.feedback?.id) return

    dispatch(
      setCanvas({
        ...canvasStore,
        feedback: { ...canvasStore.feedback, visible: !canvasStore.feedback.visible },
      })
    )

    send("managefeedback", { visible: !canvasStore.feedback?.visible })
  }

  const canChangueVisibility = () => {
    return canvasStore.feedback && isFacilitator(canvasStore.feedback.users, sessionStore.email)
  }

  const buttonSize = 30
  const spacing = 10
  const iconSize = 16
  const iconOffset = (buttonSize - iconSize) / 2
  const zoomX = windowSize.width - buttonSize - 50
  const zoomY = windowSize.height - 250

  const renderPositionButton = (iconHref: string, yOffset: number, onClick: () => void) => (
    <g transform={`translate(${zoomX}, ${zoomY + yOffset})`} style={{ cursor: "pointer" }} onClick={onClick}>
      <rect width={buttonSize} height={buttonSize} rx={6} ry={6} fill="black" />
      <image
        href={iconHref}
        width={iconSize}
        height={iconSize}
        x={iconOffset}
        y={iconOffset}
        pointerEvents="none"
      />
    </g>
  )

  if (isFeedbackCompleted) return (<></>)

  return (
    <svg
      width={resolution.width}
      height={resolution.height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 5,
      }}
    >
      <g id="toolbar" transform={`translate(${windowSize.width / 2}, ${windowSize.height - 350})`} style={{ pointerEvents: "all" }}>
        {canChangueVisibility() ? (
          <>
            <foreignObject x={-280} y={120} width={180} height={60}>
              <Timer isEditable={true} />
            </foreignObject>
            <g className="add-card-area" transform="translate(-80, 120)" style={{ cursor: "pointer" }}>
              <image
                href={canvasStore.feedback?.visible ? eyeIcon : eyeSlashIcon}
                onClick={toggleFeedbackVisibility}
              />
            </g>
          </>
        ) : (
          <>
            <foreignObject x={-110} y={120} width={90} height={60}>
              <Timer isEditable={false} />
            </foreignObject>
          </>
        )}
        <g className="add-card-area" transform="translate(0, 120)" style={{ cursor: "pointer" }}>
          <image href={addSquareIcon} onClick={() => addCard("square")} />
        </g>
        <g className="add-circle-button" transform="translate(80, 120)" style={{ cursor: "pointer" }}>
          <image href={addCircleIcon} onClick={() => addCard("circle")} />
        </g>
        {dragging && (
          <g className="trash-target" transform="translate(160, 120)" style={{ cursor: "pointer" }}>
            <image href={trashIcon} />
          </g>
        )}
      </g>

      <g style={{ pointerEvents: "all" }}>
        {renderPositionButton(plusIcon, 0, onZoomIn)}
        {renderPositionButton(negativeIcon, buttonSize + spacing, onZoomOut)}
        {renderPositionButton(
          targetIcon,
          -buttonSize - spacing,
          onCenterBoard
        )}
      </g>
    </svg>
  )
}

export default Toolbar
